// A widget visitor joins without a name, so an unnamed actor used to render as
// the literal "undefined" in the transcript.
const PLACEHOLDER_NAMES = new Set(["undefined", "null"])

export const liveChatActorName = (
	name?: string | null,
	teamName?: string | null,
) => {
	const trimmed = name?.trim()
	if (trimmed && !PLACEHOLDER_NAMES.has(trimmed.toLowerCase())) return trimmed

	const team = teamName?.trim()
	return team ? `A ${team} team member` : "A team member"
}

export const LIVE_CHAT_JOINED_SUFFIX = " joined the conversation."

export const liveChatJoinedContent = (
	name?: string | null,
	teamName?: string | null,
) => `${liveChatActorName(name, teamName)}${LIVE_CHAT_JOINED_SUFFIX}`

// A visitor joining their own chat is not an event, and a teammate reconnects
// or reopens the thread all day, so the row is written once per person.
export const shouldAnnounceLiveChatJoin = ({
	isTeamMember,
	isFinished,
	alreadyAnnounced,
}: {
	isTeamMember: boolean
	isFinished: boolean
	alreadyAnnounced: boolean
}) => isTeamMember && !isFinished && !alreadyAnnounced

export const liveChatEndedContent = (
	name?: string | null,
	teamName?: string | null,
) => `${liveChatActorName(name, teamName)} ended the conversation.`

// Rows written before the fallback existed still carry the placeholder.
export const repairLiveChatSystemContent = (
	content: string,
	teamName?: string | null,
) =>
	content.replace(/^\s*(undefined|null)\b/i, () =>
		liveChatActorName(null, teamName),
	)

// Written in English like the other system rows; the widget shows it in the
// visitor's language by matching this exact text.
export const LIVE_CHAT_MARKED_SOLVED_CONTENT =
	"Thread was resolved automatically. Reply here to ask for help."

// The sender on that row. Unread counts read the name rather than the content,
// so the request list never has to load message bodies.
export const LIVE_CHAT_MARKED_SOLVED_SENDER = "Productlane Agent"

// The rows a visitor has not seen until they open the chat: a teammate's reply,
// or the agent closing the chat for them.
export const countsAsUnreadForVisitor = (message: {
	role: string
	name?: string | null
}) =>
	message.role === "AGENT" ||
	(message.role === "SYSTEM" && message.name === LIVE_CHAT_MARKED_SOLVED_SENDER)
